import { useEffect, useState } from "react";
import { ArrowLeftRight } from "lucide-react";
import { api } from "@/lib/api";

const usd = (v) => (v == null ? "—" : `${Number(v) < 0 ? "−" : ""}$${Math.abs(Number(v)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const tone = (v) => (v == null ? "text-slate-300" : Number(v) >= 0 ? "text-emerald-300" : "text-red-300");

const Stat = ({ label, value, sub, cls = "text-slate-100", tid }) => (
  <div className="apx-card px-3 py-2 min-w-[110px]" data-testid={tid}>
    <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`font-mono-ticker font-bold text-base ${cls}`}>{value}</div>
    {sub && <div className="text-[10px] text-slate-500 font-mono-ticker">{sub}</div>}
  </div>
);

export const PaperScorecard = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/trade/coinbase/scorecard").then((r) => setS(r.data)).catch((e) => console.warn("[apex] scorecard:", e?.message || e)); }, []);
  if (!s) return null;
  const n = s.closed_trades;
  return (
    <section className="apx-card p-4 space-y-3" data-testid="paper-scorecard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display font-semibold text-base md:text-lg flex items-center gap-2"><ArrowLeftRight className="w-4 h-4 text-amber-300" /> Paper trading scorecard <span className="text-xs text-slate-500 font-sans font-normal">({n} closed trade{n === 1 ? "" : "s"})</span></h2>
        {s.last_closed_at && <span className="text-[10px] font-mono-ticker text-slate-500">last close {new Date(s.last_closed_at).toLocaleString()}</span>}
      </div>
      {n === 0 ? (
        <p className="text-xs text-slate-500" data-testid="paper-scorecard-empty">No closed paper trades yet. Once a take-profit, stop-loss or manual sell fills, your win rate, average gain vs loss and total P&L appear here.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Stat label="Win rate" value={`${s.win_rate_pct}%`} sub={`${s.wins}W · ${s.losses}L${s.breakeven ? ` · ${s.breakeven} flat` : ""}`} tid="scorecard-win-rate" />
          <Stat label="Avg gain" value={usd(s.avg_gain_usd)} sub={s.avg_gain_pct != null ? `+${s.avg_gain_pct}% avg` : "no winners yet"} cls="text-emerald-300" tid="scorecard-avg-gain" />
          <Stat label="Avg loss" value={usd(s.avg_loss_usd)} sub={s.avg_loss_pct != null ? `${s.avg_loss_pct}% avg` : "no losers yet"} cls="text-red-300" tid="scorecard-avg-loss" />
          <Stat label="Total paper P&L" value={usd(s.total_pnl_usd)} sub={s.profit_factor ? `profit factor ${s.profit_factor}` : undefined} cls={tone(s.total_pnl_usd)} tid="scorecard-total-pnl" />
          <Stat label="Best / worst" value={<span><span className="text-emerald-300">{usd(s.best_usd)}</span> <span className="text-slate-600">/</span> <span className="text-red-300">{usd(s.worst_usd)}</span></span>} tid="scorecard-best-worst" />
          <div className="apx-card px-3 py-2 text-[10px] font-mono-ticker text-slate-400 space-y-0.5" data-testid="scorecard-by-exit">
            <div className="uppercase tracking-widest text-slate-500">By exit</div>
            {Object.entries(s.by_exit).map(([k, v]) => <div key={k}><span className="text-slate-300">{k}</span> · {v.trades} ({v.wins}W) · <span className={tone(v.pnl_usd)}>{usd(v.pnl_usd)}</span></div>)}
          </div>
        </div>
      )}
      <p className="text-[10px] text-slate-500">{s.note}</p>
    </section>
  );
};
